// src/pages/MoldMaking.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter";
import { ADDITION, SUBTRACTION, Brush, Evaluator } from "three-bvh-csg";
import TutorialWindow from "../components/TutorialWindow";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import { moldMakingTutorial } from "../utils/tutorialContents";
import { CalcDraftAngles } from "../utils/geometryProcessing/CalcDraftAngles";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import { useMemo } from "react";
import { normalizeModel } from "../utils/normalizeModel";
import "../styles/common.css";

// 放在 import 语句之后
const MACHINE_PRESETS = {
  printer3D: {
    "Prusa MK3S+": { width: 250, height: 250, depth: 210 },
    "Ender 3 V2": { width: 220, height: 220, depth: 250 },
    "Ultimaker S3": { width: 230, height: 190, depth: 200 },
    Custom: { width: 10, height: 10, depth: 10 }, // 和 Sidebar 保持一致
  },
};

const DEFAULT_SCALE = 1.2; // 固定外模厚度缩放，不再给 UI

const MoldMaking = ({ stlFile }) => {
  // DOM refs
  const containerRef = useRef(null);

  // Three.js objects
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objRef = useRef(null);
  const result1Ref = useRef(null);
  const result2Ref = useRef(null);
  const positiveMeshRef = useRef(null);
  const negativeMeshRef = useRef(null);
  const highlightMesh = useRef(null);

  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [draftResults] = useState(null);
  const [error, setError] = useState(null);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [constraintWarnings, setConstraintWarnings] = useState([]); // 新增：约束警告状态
  const [gatePosition, setGatePosition] = useState({
    x: 0,
    y: 10,
    z: 0,
  });

  // 这个流程基本都算慢：打印 + 打磨 + 上脱模剂 + 浇注 + 脱模
  const fastIterNotRecommended = useMemo(() => true, []);

  const pageId = useMemo(() => `mold-making`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  const [params, setParams] = useState({
    x: 2,
    radiusTop: 3,
    radiusBottom: 1,
    cylinderHeight: 10,
    radialSegments: 32,
  });

  const [sectionsCollapsed, setSectionsCollapsed] = useState({
    basicDimensions: false,
    partingLine: false,
    gateSettings: false,
  });

  const toggleSection = useCallback((section) => {
    setSectionsCollapsed((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // Toggle tutorial visibility
  const toggleTutorial = useCallback(() => {
    setTutorialVisible((prev) => !prev);
  }, []);

  // 新增：检查模具约束
  const checkMoldConstraints = useCallback(() => {
    const constraints = window.labConstraints;
    if (!constraints || !result1Ref.current || !result2Ref.current) return;

    const printer3DSettings = constraints.machineSettings.printer3D;
    const selectedPrinter = printer3DSettings.selected;

    // 使用 constraints 里的真实尺寸（支持自定义）
    let printerDimensions = MACHINE_PRESETS.printer3D[selectedPrinter];

    // 如果是 Custom，就使用用户实际设置的尺寸（可能 Sidebar 后续会支持设置接口）
    if (selectedPrinter === "Custom") {
      printerDimensions =
        printer3DSettings.customDimensions || printerDimensions;
    }

    const warnings = [];

    // 检查上半部分模具
    const bbox1 = new THREE.Box3().setFromObject(result1Ref.current);
    const size1 = bbox1.getSize(new THREE.Vector3());

    if (
      size1.x > printerDimensions.width ||
      size1.y > printerDimensions.depth ||
      size1.z > printerDimensions.height
    ) {
      warnings.push({
        type: "size_exceeded",
        part: "Upper mold half",
        currentSize: {
          width: size1.x.toFixed(1),
          height: size1.z.toFixed(1),
          depth: size1.y.toFixed(1),
        },
        maxSize: printerDimensions,
        printer: selectedPrinter,
      });
    }

    // 检查下半部分模具
    const bbox2 = new THREE.Box3().setFromObject(result2Ref.current);
    const size2 = bbox2.getSize(new THREE.Vector3());

    if (
      size2.x > printerDimensions.width ||
      size2.y > printerDimensions.depth ||
      size2.z > printerDimensions.height
    ) {
      warnings.push({
        type: "size_exceeded",
        part: "Lower mold half",
        currentSize: {
          width: size2.x.toFixed(1),
          height: size2.z.toFixed(1),
          depth: size2.y.toFixed(1),
        },
        maxSize: printerDimensions,
        printer: selectedPrinter,
      });
    }

    setConstraintWarnings(warnings);
  }, []);

  // 关闭约束警告
  const dismissWarning = useCallback((index) => {
    setConstraintWarnings((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const analyzeDraftAngles = useCallback(() => {
    const scaleEl = document.getElementById("mold-color-scale");

    // 已经分析过，再按就清除模型和图例
    if (scaleEl) {
      // 删除颜色图例
      document.body.removeChild(scaleEl);
      const showBtn = document.getElementById("show-color-scale");
      if (showBtn) document.body.removeChild(showBtn);

      // 删除分析后的模型
      safelyDispose(positiveMeshRef);
      safelyDispose(negativeMeshRef);
      return;
    }

    // 没有分析过，就执行分析逻辑
    if (!objRef.current) return;

    // 使用修改后的函数分析模型
    splitAndAnalyzeMold(objRef.current);
  }, []);

  // Update parameters
  const updateParams = useCallback((key, value) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  const highlightArea = useCallback((type) => {
    if (!sceneRef.current) return;

    // 移除旧高亮
    if (highlightMesh.current) {
      sceneRef.current.remove(highlightMesh.current);
    }

    // 创建新几何
    const geometry = new THREE.BoxGeometry(10, 10, 10);
    const material = new THREE.MeshBasicMaterial({
      color: 0x3288e6,
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
    });

    const highlight = new THREE.Mesh(geometry, material);

    // 定义各类型的默认位置
    const positions = {
      mold_walls: new THREE.Vector3(0, 10, 0),
      parting_line: new THREE.Vector3(0, 0, 10),
      gate: new THREE.Vector3(0, 30, 0),
      gate_neck: new THREE.Vector3(0, 20, 0),
    };

    const pos = positions[type] || new THREE.Vector3(0, 0, 0);
    highlight.position.copy(pos);

    // 添加高亮
    highlightMesh.current = highlight;
    sceneRef.current.add(highlight);

    // 3秒后消失
    setTimeout(() => {
      if (highlightMesh.current && sceneRef.current) {
        sceneRef.current.remove(highlightMesh.current);
        highlightMesh.current = null;
      }
    }, 3000);
  }, []);

  // Show size warning
  const showSizeWarning = useCallback((modelSize, maxSize) => {
    const warningDiv = document.createElement("div");
    warningDiv.style.position = "fixed";
    warningDiv.style.top = "20px";
    warningDiv.style.left = "50%";
    warningDiv.style.transform = "translateX(-50%)";
    warningDiv.style.backgroundColor = "#ff9800";
    warningDiv.style.padding = "15px";
    warningDiv.style.borderRadius = "5px";
    warningDiv.style.color = "white";
    warningDiv.style.zIndex = "1000";
    warningDiv.style.boxShadow = "0 2px 5px rgba(0,0,0,0.2)";
    warningDiv.innerHTML = `
      Warning: Model size (${modelSize.x}x${modelSize.y}x${modelSize.z} cm) 
      exceeds standard print bed size (${maxSize.x}x${maxSize.y}x${maxSize.z} cm).
      Consider scaling down the model.
      <button style="float:right;border:none;background:none;color:white;font-size:20px;cursor:pointer;margin-left:10px;">×</button>
    `;

    document.body.appendChild(warningDiv);

    // Add close button functionality
    warningDiv.querySelector("button").addEventListener("click", () => {
      document.body.removeChild(warningDiv);
    });

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (document.body.contains(warningDiv)) {
        document.body.removeChild(warningDiv);
      }
    }, 10000);
  }, []);

  // Check model size
  const checkModelSize = useCallback(
    (geometry) => {
      if (!geometry) {
        console.warn("No geometry found to check size");
        return false;
      }

      try {
        geometry.computeBoundingBox();
        const boundingBox = geometry.boundingBox;
        const size = new THREE.Vector3();
        boundingBox.getSize(size);

        // Convert to centimeters (assuming model is in mm)
        const sizeInCm = size.clone().multiplyScalar(0.1);

        // Standard print bed size (30x30x30 cm)
        const maxSize = { x: 30, y: 30, z: 30 };

        if (
          sizeInCm.x > maxSize.x ||
          sizeInCm.y > maxSize.y ||
          sizeInCm.z > maxSize.z
        ) {
          showSizeWarning(sizeInCm, maxSize);
          return true;
        }
        return false;
      } catch (err) {
        console.error("Error checking model size:", err);
        return false;
      }
    },
    [showSizeWarning]
  );

  // Helper function to safely dispose objects
  const safelyDispose = useCallback((ref) => {
    if (!ref || !ref.current) return;

    try {
      if (sceneRef.current) {
        sceneRef.current.remove(ref.current);
      }

      if (ref.current.geometry) {
        ref.current.geometry.dispose();
      }

      if (ref.current.material) {
        if (Array.isArray(ref.current.material)) {
          ref.current.material.forEach((m) => {
            if (m) m.dispose();
          });
        } else {
          ref.current.material.dispose();
        }
      }

      // Clear the reference
      ref.current = null;
    } catch (err) {
      console.warn(`Error disposing object: ${err.message}`);
    }
  }, []);

  // Split model for analysis
  const splitAndAnalyzeMold = useCallback(
    (mesh) => {
      if (!mesh || !mesh.geometry || !sceneRef.current) {
        console.warn("Cannot analyze mold: mesh or scene not available");
        return;
      }

      try {
        const geometry = mesh.geometry.clone();
        const zThreshold = 0; // Cutting plane's z value

        // Check mesh for analysis
        checkModelSize(geometry);

        // Create two separate geometries for positive and negative direction
        const positiveGeometry = new THREE.BufferGeometry();
        const negativeGeometry = new THREE.BufferGeometry();

        const positionAttribute = geometry.getAttribute("position");
        if (!positionAttribute) {
          console.warn("Position attribute missing in geometry");
          return;
        }

        const vertices = positionAttribute.array;

        const positiveVertices = [];
        const negativeVertices = [];

        // Separate vertices to positive and negative direction
        for (let i = 0; i < vertices.length; i += 9) {
          const v0 = new THREE.Vector3(
            vertices[i],
            vertices[i + 1],
            vertices[i + 2]
          );
          const v1 = new THREE.Vector3(
            vertices[i + 3],
            vertices[i + 4],
            vertices[i + 5]
          );
          const v2 = new THREE.Vector3(
            vertices[i + 6],
            vertices[i + 7],
            vertices[i + 8]
          );

          if (v0.z > zThreshold && v1.z > zThreshold && v2.z > zThreshold) {
            positiveVertices.push(
              ...v0.toArray(),
              ...v1.toArray(),
              ...v2.toArray()
            );
          } else if (
            v0.z < zThreshold &&
            v1.z < zThreshold &&
            v2.z < zThreshold
          ) {
            negativeVertices.push(
              ...v0.toArray(),
              ...v1.toArray(),
              ...v2.toArray()
            );
          }
        }

        // Create geometries if we have vertices
        if (positiveVertices.length > 0) {
          positiveGeometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(positiveVertices, 3)
          );
        }

        if (negativeVertices.length > 0) {
          negativeGeometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(negativeVertices, 3)
          );
        }

        // Remove old meshes
        safelyDispose(positiveMeshRef);
        safelyDispose(negativeMeshRef);

        // Create and add positive mesh if we have vertices
        if (positiveVertices.length > 0) {
          const positiveMesh = new THREE.Mesh(
            positiveGeometry,
            mesh.material.clone()
          );

          // 几何体必须有法线
          positiveGeometry.computeVertexNormals();

          // Align positive mesh to y=0
          const positiveBoundingBox = new THREE.Box3().setFromObject(
            positiveMesh
          );
          const positiveMinY = positiveBoundingBox.min.y;
          positiveMesh.position.y -= positiveMinY;

          // 使用 CalcDraftAngles 模块分析斜度角
          const draftDirection = new THREE.Vector3(0, 0, 1);
          CalcDraftAngles.analyze(positiveMesh, draftDirection); // 移除未使用的赋值

          // 设置位置
          positiveMesh.position.z += 20;

          positiveMeshRef.current = positiveMesh;
          sceneRef.current.add(positiveMesh);
        }

        // Create and add negative mesh if we have vertices
        if (negativeVertices.length > 0) {
          const negativeMesh = new THREE.Mesh(
            negativeGeometry,
            mesh.material.clone()
          );

          // 几何体必须有法线
          negativeGeometry.computeVertexNormals();

          // Align negative mesh to y=0
          const negativeBoundingBox = new THREE.Box3().setFromObject(
            negativeMesh
          );
          const negativeMinY = negativeBoundingBox.min.y;
          negativeMesh.position.y -= negativeMinY;

          // 使用 CalcDraftAngles 模块分析斜度角
          const draftDirection = new THREE.Vector3(0, 0, -1);
          CalcDraftAngles.analyze(negativeMesh, draftDirection);

          // 设置位置
          negativeMesh.position.z -= 20;

          negativeMeshRef.current = negativeMesh;
          sceneRef.current.add(negativeMesh);
        }

        // 最重要的部分 - 使用 CalcDraftAngles 创建颜色图例
        // 注意：确保使用 "mold-color-scale" 作为 ID
        CalcDraftAngles.createColorLegend("mold-color-scale");
      } catch (err) {
        console.error("Error analyzing mold:", err);
      }
    },
    [safelyDispose, checkModelSize]
  );

  // Create result mesh 1 (top half)
  const createResultMesh1 = useCallback(
    (result, m1) => {
      if (!sceneRef.current) return;

      // Remove existing mesh
      safelyDispose(result1Ref);

      // Create new mesh
      try {
        const resultMesh = new THREE.Mesh(result.geometry, result.material);
        resultMesh.position.z = m1;
        resultMesh.position.y = m1 / 2 + 1;
        resultMesh.castShadow = true;
        resultMesh.receiveShadow = true;

        result1Ref.current = resultMesh;
        sceneRef.current.add(resultMesh);
      } catch (err) {
        console.error("Error creating result mesh 1:", err);
      }
    },
    [safelyDispose]
  );

  // Create result mesh 2 (bottom half)
  const createResultMesh2 = useCallback(
    (result, m2) => {
      if (!sceneRef.current) return;

      // Remove existing mesh
      safelyDispose(result2Ref);

      // Create new mesh
      try {
        const resultMesh = new THREE.Mesh(result.geometry, result.material);
        resultMesh.position.z = m2;
        resultMesh.position.y = -m2 / 2 + 1;
        resultMesh.castShadow = true;
        resultMesh.receiveShadow = true;

        result2Ref.current = resultMesh;
        sceneRef.current.add(resultMesh);
      } catch (err) {
        console.error("Error creating result mesh 2:", err);
      }
    },
    [safelyDispose]
  );

  // Process the mold
  const processMoldMaking = useCallback(() => {
    if (!objRef.current || !sceneRef.current) {
      console.warn("Cannot process mold making: Object or scene not available");
      return;
    }

    try {
      // Update status
      setIsLoading(true);
      setProcessingStatus("Starting mold creation...");

      // Hide the original model
      objRef.current.visible = false;

      // Create material for colorization
      const boundingMaterial = new THREE.MeshStandardMaterial({
        flatShading: true,
        polygonOffset: true,
        polygonOffsetUnits: 1,
        polygonOffsetFactor: 1,
        transparent: true,
        opacity: 0.9,
      });

      const obj = objRef.current;

      // Ensure geometry has normals before creating brush
      if (!obj.geometry.attributes.normal) {
        obj.geometry.computeVertexNormals();
      }

      // Create object brush
      const object_brush = new Brush(
        obj.geometry,
        new THREE.MeshStandardMaterial({
          color: 0x80cbc4,
          polygonOffset: true,
          polygonOffsetUnits: 1,
          polygonOffsetFactor: 1,
        })
      );

      // Ensure world matrix is updated
      obj.geometry.computeVertexNormals();
      object_brush.updateMatrixWorld();

      // Get model dimensions
      obj.geometry.computeBoundingBox();
      const bb = obj.geometry.boundingBox;
      const bb_size = new THREE.Vector3();
      bb.getSize(bb_size);

      const scale = DEFAULT_SCALE;
      const x = params.x;

      setProcessingStatus("Creating bounding boxes...");
      // Create bounding box brushes
      const bb_geom1 = new THREE.BoxGeometry(
        bb_size.x * scale,
        bb_size.y * scale,
        (bb_size.z * scale) / x
      );

      const bb_geom2 = new THREE.BoxGeometry(
        bb_size.x * scale,
        bb_size.y * scale,
        bb_size.z * scale * (1 - 1 / x)
      );

      const bb_brush = new Brush(bb_geom1, boundingMaterial.clone());
      bb_brush.position.z += (bb_size.z * scale * (x - 1)) / (2 * x);
      bb_brush.updateMatrixWorld();

      const bb_brush2 = new Brush(bb_geom2, boundingMaterial.clone());
      bb_brush2.position.z -= (bb_size.z * scale) / (2 * x);
      bb_brush2.updateMatrixWorld();

      setProcessingStatus("Creating air vent...");
      // Find highest point for vent
      const maxY = obj.geometry.boundingBox.max.y;
      const positions = obj.geometry.attributes.position.array;
      const highestPoint = new THREE.Vector3();

      for (let i = 0; i < positions.length; i += 3) {
        const py = positions[i + 1];
        if (py === maxY) {
          highestPoint.set(positions[i], py, positions[i + 2]);
          break;
        }
      }

      // Create vent cylinder
      const airVent = new THREE.CylinderGeometry(
        params.radiusTop,
        params.radiusBottom,
        (bb_size.y / 2) * 1.5,
        params.radialSegments
      );

      const vent = new Brush(
        airVent,
        new THREE.MeshStandardMaterial({
          color: 0x80cbc4,
          polygonOffset: true,
          polygonOffsetUnits: 1,
          polygonOffsetFactor: 1,
        })
      );
      vent.castShadow = true;
      vent.position.set(
        highestPoint.x + gatePosition.x,
        highestPoint.y / 2 + 20 + gatePosition.y,
        highestPoint.z + gatePosition.z
      );
      vent.updateMatrixWorld();

      try {
        // Boolean operations
        const evaluator = new Evaluator();
        setProcessingStatus("Creating air vent and combining with model...");
        console.log("Performing first boolean operation (ADDITION)...");
        const inner = evaluator.evaluate(vent, object_brush, ADDITION);

        setProcessingStatus("Creating upper mold half...");
        console.log("Creating upper mold half...");
        const result = evaluator.evaluate(bb_brush, inner, SUBTRACTION);
        const m1 = bb_size.y * 1.1;

        setProcessingStatus("Creating lower mold half...");
        console.log("Creating lower mold half...");
        const result2 = evaluator.evaluate(bb_brush2, inner, SUBTRACTION);
        const m2 = -bb_size.y * 1.1;

        // Create meshes from results
        createResultMesh1(result, m1);
        createResultMesh2(result2, m2);

        setProcessingStatus("Completed!");
        // 检查约束（新增）
        setTimeout(() => {
          checkMoldConstraints();
        }, 500);
        // Clear status after a delay
        setTimeout(() => {
          setProcessingStatus("");
          setIsLoading(false);
        }, 2000);
      } catch (err) {
        console.error("Error during boolean operations:", err);
        setError(`Failed to create mold: ${err.message}`);
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Error processing mold:", err);
      setError(`Failed to process mold: ${err.message}`);
      setIsLoading(false);
    }
  }, [
    params,
    gatePosition,
    createResultMesh1,
    createResultMesh2,
    checkMoldConstraints, // 添加依赖
  ]);

  // Helper to save strings as files
  const saveString = useCallback((text, filename) => {
    const blob = new Blob([text], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();

    // Clean up URL object
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
    }, 100);
  }, []);

  // Export both mold halves
  const exportBoth = useCallback(() => {
    if (!result1Ref.current || !result2Ref.current) {
      console.warn("Cannot export: mold halves not created yet");
      setError(
        "Mold halves not ready for export. Please wait for processing to complete."
      );
      return;
    }

    try {
      const exporter = new STLExporter();

      const resultLeft = exporter.parse(result1Ref.current);
      saveString(resultLeft, "moldmakingleft.stl");

      const resultRight = exporter.parse(result2Ref.current);
      saveString(resultRight, "moldmakingright.stl");

      // Show tutorial after export
      setTutorialVisible(true);
    } catch (err) {
      console.error("Error exporting mold:", err);
      setError(`Failed to export mold: ${err.message}`);
    }
  }, [saveString]); // 添加依赖

  // Load STL model
  const loadModel = useCallback(() => {
    setIsLoading(true);
    setError(null);

    // Clean up previous model
    safelyDispose(objRef);

    // Clean up previous result meshes
    [result1Ref, result2Ref, positiveMeshRef, negativeMeshRef].forEach(
      safelyDispose
    );

    // Remove color scale
    const colorScale = document.getElementById("mold-color-scale");
    if (colorScale) {
      document.body.removeChild(colorScale);
    }

    const showButton = document.getElementById("show-color-scale");
    if (showButton) {
      document.body.removeChild(showButton);
    }

    // Use default model if none provided
    const modelPath = stlFile || "/models/default.stl";
    setProcessingStatus(`Loading model from ${modelPath.split("/").pop()}...`);

    const loader = new STLLoader();

    loader.load(
      modelPath,
      (geometry) => {
        try {
          // Check if scene exists
          if (!sceneRef.current) {
            console.error("Scene not initialized");
            setError("Scene not initialized");
            setIsLoading(false);
            return;
          }

          // Ensure the geometry has UVs (IMPORTANT FIX)
          if (!geometry.attributes.uv) {
            const uvs = new Float32Array(
              geometry.attributes.position.count * 2
            );
            geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
          }

          // Create material for original model
          const material = new THREE.MeshStandardMaterial({
            side: THREE.DoubleSide,
            vertexColors: true,
          });

          const mesh = new THREE.Mesh(geometry, material);

          normalizeModel(mesh);

          // Store mesh reference
          objRef.current = mesh;

          // Process mold making
          processMoldMaking();
        } catch (err) {
          console.error("Error processing loaded model:", err);
          setError(`Error processing model: ${err.message}`);
          setIsLoading(false);
        }
      },
      (xhr) => {
        if (xhr.total > 0) {
          const percentComplete = (xhr.loaded / xhr.total) * 100;
          setProcessingStatus(`Loading model: ${Math.round(percentComplete)}%`);
        }
      },
      (error) => {
        console.error(`Error loading STL: ${error}`);
        setError(`Failed to load model: ${error.message}`);
        setIsLoading(false);
      }
    );
  }, [stlFile, processMoldMaking, safelyDispose]);

  // Initialize Three.js scene
  useEffect(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    // Load model after scene initialization
    setTimeout(() => {
      loadModel();
    }, 100);

    return () => {
      if (cleanup) cleanup();

      // Clean up color scale and warnings
      const colorScale = document.getElementById("mold-color-scale");
      if (colorScale) {
        document.body.removeChild(colorScale);
      }

      const showButton = document.getElementById("show-color-scale");
      if (showButton) {
        document.body.removeChild(showButton);
      }
    };
  }, [loadModel]);

  // Re-load model when stlFile changes
  useEffect(() => {
    if (stlFile && sceneRef.current) {
      loadModel();
    }
  }, [stlFile, loadModel]);

  // Re-process when parameters change
  useEffect(() => {
    if (objRef.current) {
      processMoldMaking();
    }
  }, [params, gatePosition, processMoldMaking]);

  // Add this useEffect hook to your component
  useEffect(() => {
    const handleTooltipPosition = () => {
      document.querySelectorAll(".tooltip").forEach((tooltip) => {
        const iconRect = tooltip.getBoundingClientRect();
        const tooltipText = tooltip.querySelector(".tooltip-text");

        if (tooltipText) {
          // Reset classes
          tooltip.classList.remove("left-edge", "right-edge");

          // Calculate potential position
          const tooltipWidth = 240; // Match your CSS width
          const leftPosition =
            iconRect.left + iconRect.width / 2 - tooltipWidth / 2;
          const rightPosition = leftPosition + tooltipWidth;

          // Check if tooltip would go offscreen
          if (leftPosition < 10) {
            // Near left edge
            tooltip.classList.add("left-edge");
          } else if (rightPosition > window.innerWidth - 10) {
            // Near right edge
            tooltip.classList.add("right-edge");
          }
        }
      });
    };

    // Run on mount and window resize
    handleTooltipPosition();
    window.addEventListener("resize", handleTooltipPosition);

    return () => {
      window.removeEventListener("resize", handleTooltipPosition);
    };
  }, []);

  return (
    <div className="container">
      <button
        className="panel-toggle-button"
        onClick={() => {
          console.log("Button clicked, expanded will be:", !expanded); // 调试日志
          setExpanded(!expanded);
        }}
      >
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      <div ref={containerRef} className="canvas-container"></div>

      <div>
        {showAnalysis && draftResults && (
          <div className="analysis-popup">
            <h3>Draft Analysis Result</h3>
            <p>
              Quality:{" "}
              <span
                className={
                  draftResults.score === "good"
                    ? "score-good"
                    : draftResults.score === "warning"
                    ? "score-warning"
                    : "score-bad"
                }
              >
                {draftResults.score.toUpperCase()}
              </span>
            </p>

            <p>
              {draftResults.problems.length} Problem Area
              {draftResults.problems.length > 1 ? "s" : ""}:
            </p>
            <ul style={{ paddingLeft: "20px" }}>
              {draftResults.problems.map((p, idx) => (
                <li key={idx}>
                  <strong>{p.location}</strong>: {p.description}
                </li>
              ))}
            </ul>

            <div className="popup-buttons">
              <button onClick={() => setShowAnalysis(false)}>CONTINUE</button>
            </div>
          </div>
        )}

        {/* Loading message */}
        {isLoading && (
          <div className="loading-box">
            {processingStatus || "Loading model..."}
          </div>
        )}

        {/* Error message */}
        {error && <div className="error-box">Error: {error}</div>}

        {/* 约束警告 */}
        {constraintWarnings.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "80px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1002,
              maxWidth: "90%",
            }}
          >
            {constraintWarnings.map((warning, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "#ff9800",
                  color: "white",
                  padding: "12px 16px",
                  borderRadius: "6px",
                  marginBottom: "8px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                  fontSize: "13px",
                }}
              >
                <strong>⚠️ {warning.part}</strong> exceeds {warning.printer} bed
                size
                <br />
                Current: {warning.currentSize.width}×{warning.currentSize.depth}
                ×{warning.currentSize.height}mm
                <br />
                Max: {warning.maxSize.width}×{warning.maxSize.depth}×
                {warning.maxSize.height}mm
                <button
                  onClick={() => dismissWarning(index)}
                  style={{
                    float: "right",
                    background: "none",
                    border: "none",
                    color: "white",
                    fontSize: "16px",
                    cursor: "pointer",
                    marginLeft: "10px",
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

{/* Controls Panel（统一风格，只有一条白底标题） */}
{expanded && (
  <div className={`controls-panel ${panelCollapsed ? 'collapsed' : ''}`}>
    <div
      className="panel-title-row"
      onClick={() => setPanelCollapsed(!panelCollapsed)}
      style={{ cursor: 'pointer' }}
    >
      <div className="panel-title">Mold Making</div>
      {fastIterNotRecommended && (
        <span
          className="iteration-badge badge--warn"
          title="3D printing + mold prep + casting = long manual time"
        >
          Not ideal for quick iteration
        </span>
      )}
      <span className="collapse-icon">{panelCollapsed ? '▼' : '▲'}</span>
    </div>

    {!panelCollapsed && (
      <div className="panel-content">
        <div className="info-box">
          <p style={{ margin: '0 0 10px 0' }}>
        This tool creates a two-part mold from your 3D model using boolean
        operations, designed for 3D printing. The printed mold can then be used
        to cast materials such as wax or silicone. Use the sliders below to
        adjust mold wall thickness, parting line position, and gate size.
      </p>
      <p style={{ fontStyle: 'italic', fontSize: '13px', margin: 0 }}>
        Tip: After exporting, 3D print each half separately and use the
        alignment guides for accurate assembly before pouring your casting
        material.
      </p>
        </div>

        {/* Time & Effort（保留一次徽章就行，这里不要再放小徽章了） */}
        <div className="panel-section" style={{ marginBottom: 15 }}>
          <div className="section-header">
            <h4 style={{ margin: 0, fontSize: 14 }}>Time & Effort</h4>
          </div>
          <div className="section-content" style={{ fontSize: 13 }}>
            <ul style={{ marginLeft: 16, lineHeight: 1.5 }}>
              <li><strong>3D Printing</strong> (two-part mold) + support removal + surface sanding + alignment correction.</li>
              <li><strong>Mold Preparation</strong>: Applying release agent, positioning, edge sealing; time varies with size/detail.</li>
              <li><strong>Casting</strong>: Mixing, vacuum/vibration, curing; often multiple test batches.</li>
              <li><strong>Demolding & Post-Processing</strong>: Trimming, sanding, washing/coating; may need rework.</li>
              <li>Overall <strong>hard to estimate</strong>, <strong>not great for rapid iteration</strong>.</li>
            </ul>
            <p style={{ fontSize: 12, opacity: 0.85, marginTop: 8 }}>
              Recommendation: Try scaled-down/low-res prints to validate parting lines & gates first.
            </p>
          </div>
        </div>

        <div className="buttons-container">
          <button
            onClick={exportBoth}
            disabled={isLoading || !result1Ref.current || !result2Ref.current}
            className="action-button"
          >
            EXPORT
          </button>
          <button
            onClick={analyzeDraftAngles}
            disabled={!result1Ref.current}
            className="action-button"
          >
            ANALYZE DRAFT ANGLES
          </button>
          <button onClick={toggleTutorial} className="action-button">
            {tutorialVisible ? "HIDE INTERACTIVE GUIDANCE" : "SHOW INTERACTIVE GUIDANCE"}
          </button>
        </div>
      </div>
    )}
  </div>
)}


        {/* Tutorial Window */}
        {/* Tutorial Window (force on top) */}
        {tutorialVisible && (
          <div className="tutorial-overlay" onClick={toggleTutorial}>
            <div onClick={(e) => e.stopPropagation()}>
              <TutorialWindow
                visible={tutorialVisible}
                onToggleVisibility={toggleTutorial}
                tutorialContent={moldMakingTutorial}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoldMaking;
